import { useState } from 'react';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dahbam3p14a1h3mr6hi0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get('website_alt') ?? '').trim();
    if (guard) {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        '';
      const code = parsed?.code ?? '';
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(responseText);

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
        setMessageLength(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试或直接发送邮件给我们。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交未成功，请检查网络后重试。');
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">
            称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于回复你的来信"
            className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-800 mb-2">
          来信类型
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue="提供话题线索"
          className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
        >
          <option value="提供话题线索">提供话题线索</option>
          <option value="内容纠错">内容纠错</option>
          <option value="分类建议">分类建议</option>
          <option value="合作与授权">合作与授权</option>
        </select>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800">
            具体说明
          </label>
          <span className="text-xs text-foreground-500">{messageLength}/500</span>
        </div>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          onChange={(e) => setMessageLength(e.target.value.length)}
          placeholder="请描述话题所属分类与可公开查证的背景，我们会认真阅读每一封来信。"
          className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
        ></textarea>
      </div>

      <input
        className="field-guard"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
        >
          <i className="ri-send-plane-line"></i>
          {status === 'submitting' ? '正在提交…' : '提交来信'}
        </button>
        <p className="text-xs text-foreground-500">
          我们会在工作日内回复较为具体的来信。
        </p>
      </div>

      {status === 'success' && (
        <div className="mt-5 flex items-start gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line mt-0.5"></i>
          <span>来信已收到，感谢你的贡献，我们会在工作日内查看。</span>
        </div>
      )}
      {status === 'error' && (
        <div className="mt-5 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{formError}</span>
        </div>
      )}
    </form>
  );
}